import { Package } from "lucide-react";
import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

import type { Survivor } from "../types";
import {
  fallbackSurvivorArt,
  portraitArtFor,
  resolvedAppearance,
  safeArtToken,
} from "./survivorArt";

interface SurvivorPortraitProps {
  survivor: Survivor;
  className?: string;
  decorative?: boolean;
  detail?: "hero" | "thumb";
  priority?: boolean;
}

export function SurvivorPortrait({
  survivor,
  className = "",
  decorative = true,
  detail = "hero",
  priority = false,
}: SurvivorPortraitProps) {
  const appearance = resolvedAppearance(survivor);
  const recipe = appearance.recipe;
  const injured = survivor.injuries.length > 0 || survivor.health < 60;
  const requestedArt = portraitArtFor(survivor, detail);
  const [source, setSource] = useState(requestedArt);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    setSource(requestedArt);
    setUnavailable(false);
  }, [requestedArt]);

  const classNames = [
    "portrait",
    "survivor-portrait",
    `portrait-${survivor.portrait_variant}`,
    `skin-${safeArtToken(recipe.skin)}`,
    `hair-${safeArtToken(recipe.hair)}`,
    `outfit-${safeArtToken(recipe.outfit)}`,
    survivor.alive ? `status-${survivor.status}` : "status-dead",
    injured ? "is-injured" : "",
    survivor.equipment.length ? "has-equipment" : "",
    className,
  ].filter(Boolean).join(" ");
  const style = {
    "--survivor-phase": `${-appearance.motion_phase}ms`,
    "--portrait-injury-art": `url("${fallbackSurvivorArt.injuredOverlay}")`,
  } as CSSProperties;
  const accessibility = decorative
    ? { "aria-hidden": true as const }
    : { role: "img", "aria-label": `${survivor.name}, ${survivor.role}` };

  function recoverFromMissingArt() {
    if (source !== fallbackSurvivorArt.portrait) {
      setSource(fallbackSurvivorArt.portrait);
      return;
    }
    setUnavailable(true);
  }

  return (
    <span
      className={classNames}
      style={style}
      data-appearance-profile={appearance.profile_id ?? "procedural"}
      data-art-detail={detail}
      data-survivor-status={survivor.alive ? survivor.status : "dead"}
      {...accessibility}
    >
      {!unavailable && (
        <img
          className="survivor-raster-portrait"
          src={source}
          alt=""
          width={512}
          height={640}
          decoding="async"
          draggable={false}
          loading={priority ? "eager" : "lazy"}
          onError={recoverFromMissingArt}
        />
      )}
      <span className="portrait-condition-overlay" aria-hidden="true" />
      {!survivor.alive && <span className="portrait-status-mark" aria-hidden="true" />}
      {survivor.equipment.length > 0 && (
        <span className="portrait-equipment-mark" title={survivor.equipment.join(", ")} aria-hidden="true">
          <Package size={9} strokeWidth={2.4} />
        </span>
      )}
    </span>
  );
}
